import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import GolferAuthLayout from './GolferAuthLayout';

// Check for common email typos and suggest correction
const checkEmailTypo = (email) => {
  const lower = email.toLowerCase().trim();

  const badEndings = ['.con', '.cpm', '.vom', '.ocm', '.ner', '.ent', '.ogr'];
  const goodEnding = { '.con': '.com', '.cpm': '.com', '.vom': '.com', '.ocm': '.com',
                       '.ner': '.net', '.ent': '.net', '.ogr': '.org' };

  const providerFixes = {
    'gmial.com': 'gmail.com', 'gimal.com': 'gmail.com', 'gamil.com': 'gmail.com',
    'gnail.com': 'gmail.com', 'gmaill.com': 'gmail.com', 'hotmal.com': 'hotmail.com',
    'hotmial.com': 'hotmail.com', 'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com',
    'outlok.com': 'outlook.com', 'iclould.com': 'icloud.com', 'icoud.com': 'icloud.com'
  };

  const atIndex = lower.lastIndexOf('@');
  if (atIndex === -1) return { hasTypo: false };

  const domain = lower.slice(atIndex + 1);
  const localPart = lower.slice(0, atIndex);

  if (providerFixes[domain]) {
    return { hasTypo: true, suggestion: `${localPart}@${providerFixes[domain]}` };
  }

  for (const bad of badEndings) {
    if (domain.includes(bad)) {
      const fixedDomain = domain.slice(0, domain.indexOf(bad)) + goodEnding[bad];
      return { hasTypo: true, suggestion: `${localPart}@${fixedDomain}` };
    }
  }

  return { hasTypo: false };
};

const Register = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailWarning, setEmailWarning] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref);
    }
  }, [location]);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (newEmail.length > 5) {
      const typoCheck = checkEmailTypo(newEmail);
      if (typoCheck.hasTypo) {
        setEmailWarning(`Did you mean ${typoCheck.suggestion}?`);
      } else {
        setEmailWarning(null);
      }
    } else {
      setEmailWarning(null);
    }
  };

  const applySuggestion = () => {
    const typoCheck = checkEmailTypo(email);
    if (typoCheck.hasTypo) {
      setEmail(typoCheck.suggestion);
      setConfirmEmail(typoCheck.suggestion);
      setEmailWarning(null);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
      setError("Emails do not match. Please check and try again.");
      return;
    }

    const typoCheck = checkEmailTypo(email);
    if (typoCheck.hasTypo) {
      setError(`Your email appears to contain a typo. Did you mean ${typoCheck.suggestion}?`);
      return;
    }

    try {
      await axios.post(
        "https://lionfish-app-w73zu.ondigitalocean.app/api/register/",
        {
          username,
          password,
          confirm_password: confirmPassword,
          email,
          referral_code: referralCode
        }
      );

      setMessage("User Created - Redirecting to login...");
      setTimeout(() => {
        navigate("/login", { state: { username } });
      }, 2000);

    } catch (error) {
      console.error("Registration failed:", error);
      if (error.response?.data?.errors) {
        setError("Error: " + JSON.stringify(error.response.data.errors));
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <GolferAuthLayout
      seoTitle="Register — PrivateIQ"
      seoDescription="Create your PrivateIQ account to access private golf club data, insights, and community forums."
      canonicalPath="/register"
      hideGetStarted
    >
      <div className="ga-auth-card wide">
        <div className="ga-auth-badge">Become a Member</div>
        <h1 className="ga-auth-title">Create Account</h1>
        <p className="ga-auth-subtitle">
          Get access to private golf club data, insights, and community.
        </p>

        {message && <p className="ga-auth-success">{message}</p>}
        {error && <p className="ga-auth-error">{error}</p>}

        {referralCode && (
          <div className="ga-referral-banner">
            You were referred by a friend! Complete signup to give them a reward.
          </div>
        )}

        <form onSubmit={handleRegister} className="ga-auth-form">
          <input
            className="ga-auth-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            required
          />
          <input
            className="ga-auth-input"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Email"
            required
          />
          {emailWarning && (
            <div className="ga-email-warning">
              <span>{emailWarning}</span>
              <button type="button" onClick={applySuggestion} className="ga-suggestion-btn">
                Yes, fix it
              </button>
            </div>
          )}
          <input
            className="ga-auth-input"
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Confirm Email"
            required
          />
          <input
            className="ga-auth-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
          />
          <input
            className="ga-auth-input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
            required
          />
          <button type="submit" className="ga-auth-btn">Register</button>
        </form>

        <div className="ga-auth-links">
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </div>

        {/* Collapsible Disclaimer */}
        <div className="ga-disclaimer">
          <div className="ga-disclaimer-header" onClick={() => setIsDisclaimerOpen(!isDisclaimerOpen)}>
            <div className="ga-disclaimer-icon">
              <span>ℹ️</span>
              <span>Important Disclaimer</span>
            </div>
            <span className="ga-disclaimer-toggle">{isDisclaimerOpen ? '▲' : '▼'}</span>
          </div>

          {isDisclaimerOpen && (
            <div className="ga-disclaimer-content">
              <p>
                By accessing this site, you agree to our <Link to="/termsofservice">Terms of Service</Link>.
              </p>
              <p>
                <strong>PrivateIQ</strong> is an independent platform that provides a directory and review service for
                individuals. In addition, we provide private clubs we partner with the opportunity to connect with potential members in
                their area, <strong>at the discretion of the individual.</strong>
              </p>
              <p>
                All information provided is for general informational purposes only and should be independently verified.
                PrivateIQ does not guarantee the accuracy, completeness, or reliability of any club-related details,
                including but not limited to:
              </p>
              <ul>
                <li><strong>Membership fees</strong></li>
                <li><strong>Amenities</strong></li>
                <li><strong>Access policies</strong></li>
              </ul>
              <p>
                Information may be outdated, incomplete, or subject to change without notice.
              </p>
              <p>
                PrivateIQ assumes no responsibility for any decisions made based on the content provided on this site.
                Users should conduct their own due diligence before making any financial or membership decisions.
              </p>
              <p>
                <strong>PrivateIQ makes no warranties or representations, express or implied, regarding the content on this site.</strong>
              </p>
              <p>
                All use of this site is at your own risk. PrivateIQ shall not be liable for any direct, indirect,
                incidental, consequential, or punitive damages arising from your use of this site.
              </p>
              <p>
                If you believe corrections are needed, please contact us at <a href="mailto:support@privateiq.golf">support@privateiq.golf</a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </GolferAuthLayout>
  );
};

export default Register;
